import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { relativeTime } from "../lib/format";
import { EmptyState } from "../components/EmptyState";
import { SkeletonPage } from "../components/Skeleton";
import { useLiveVisitFeed } from "../hooks/useLiveVisitFeed";

type Dash = {
  recentVisits: Array<{
    email?: string;
    name?: string;
    room_name: string;
    room_id: string;
    started_at: string;
    dwell_ms: number;
    is_internal: number;
    view_id: string;
  }>;
  mostEngaged: Array<{
    email: string;
    name?: string;
    score: number;
    label: string;
    room_name: string;
    room_id?: string;
    momentum_7d: number;
  }>;
  visitStats: Array<{ day: string; visits: number }>;
  summary?: { totalVisits: number; uniqueVisitors: number; avgVisitsPerDay: number };
};

function formatDwell(ms: number) {
  const s = Math.round(Number(ms || 0) / 1000);
  if (s <= 0) return "Under 1 min";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem ? `${m}m ${rem}s` : `${m}m`;
}

function formatDayLabel(isoDay: string) {
  const d = new Date(`${isoDay}T12:00:00Z`);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function DashboardPage() {
  const [data, setData] = useState<Dash | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showAllVisits, setShowAllVisits] = useState(false);
  const { banner: liveVisit, connected: liveConnected } = useLiveVisitFeed("/api/v1/analytics/live", !err);

  useEffect(() => {
    api<Dash>("/api/v1/analytics/dashboard")
      .then(setData)
      .catch((e) => setErr(String(e.message || e)));
  }, []);

  useEffect(() => {
    if (err) return;
    const poll = window.setInterval(() => {
      api<Dash>("/api/v1/analytics/dashboard")
        .then(setData)
        .catch(() => undefined);
    }, 60000);
    return () => window.clearInterval(poll);
  }, [err]);

  if (err)
    return (
      <div className="section">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-lead">We couldn't load engagement data.</p>
        <p className="muted">{err}</p>
        <button type="button" className="secondary" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  if (!data) return <SkeletonPage />;

  const hasActivity =
    data.recentVisits.length > 0 ||
    data.mostEngaged.length > 0 ||
    data.visitStats.some((v) => Number(v.visits) > 0);

  if (!hasActivity) {
    return (
      <div>
        <h1 className="page-title">Dashboard</h1>
        <p className="page-lead">Visits and engagement will appear once you share a Space.</p>
        <EmptyState
          headline="Nothing to report yet"
          body="Create a Space, upload documents, and share a link. Activity shows up here."
          primaryLabel="Go to Spaces"
          onPrimary={() => {
            window.location.href = "/rooms";
          }}
        />
      </div>
    );
  }

  const maxVisits = Math.max(1, ...data.visitStats.map((v) => Number(v.visits)));
  const totalVisits = data.summary?.totalVisits ?? data.visitStats.reduce((n, v) => n + Number(v.visits), 0);
  const uniqueVisitors = data.summary?.uniqueVisitors ?? 0;
  const avgPerDay = data.summary?.avgVisitsPerDay ?? Math.round((totalVisits / 30) * 10) / 10;
  const firstDay = data.visitStats[0]?.day;
  const lastDay = data.visitStats[data.visitStats.length - 1]?.day;
  const visitsShown = showAllVisits ? data.recentVisits : data.recentVisits.slice(0, 8);

  return (
    <div>
      {liveVisit ? (
        <div className="live-visit-banner" role="status">
          <span className={`live-visit-banner__dot${liveConnected ? " is-connected" : ""}`} aria-hidden="true" />
          {liveVisit}
        </div>
      ) : null}
      <h1 className="page-title">Dashboard</h1>
      <p className="page-lead">Who is opening your rooms and how deeply they are reading.</p>

      <div className="stat-cards">
        <div className="stat-card">
          <span className="stat-card__value">{totalVisits}</span>
          <span className="stat-card__label">Visits (30d)</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__value">{uniqueVisitors}</span>
          <span className="stat-card__label">Unique visitors</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__value">{avgPerDay}</span>
          <span className="stat-card__label">Avg per day</span>
        </div>
      </div>

      <section className="section">
        <div className="section-head">
          <h2>Last 30 days</h2>
        </div>
        <div className="sparkline-wrap">
          {totalVisits > 0 ? (
            <>
              <span className="sparkline__peak-line" aria-hidden="true" />
              <span className="sparkline__peak" aria-hidden="true">
                Peak {maxVisits}
              </span>
            </>
          ) : null}
          <div
            className="sparkline"
            role="group"
            aria-label={`Daily visits for the last 30 days. Total ${totalVisits} visits. Peak day ${maxVisits}.`}
          >
            {data.visitStats.map((v) => {
              const visits = Number(v.visits);
              const pct = visits === 0 ? 0 : Math.max(10, (visits / maxVisits) * 100);
              const tip = `${formatDayLabel(v.day)} · ${visits} visit${visits === 1 ? "" : "s"}`;
              return (
                <button
                  key={v.day}
                  type="button"
                  className={`sparkline__slot${visits === 0 ? " sparkline__slot--zero" : ""}`}
                  data-tip={tip}
                  aria-label={`${formatDayLabel(v.day)}: ${visits} visit${visits === 1 ? "" : "s"}`}
                >
                  <span
                    className="sparkline__bar"
                    style={visits === 0 ? undefined : { height: `${pct}%` }}
                  />
                </button>
              );
            })}
          </div>
          {firstDay && lastDay ? (
            <div className="sparkline__axis" aria-hidden="true">
              <span className="sparkline__axis-start">{formatDayLabel(firstDay)}</span>
              <span className="sparkline__axis-end">{formatDayLabel(lastDay)}</span>
            </div>
          ) : null}
        </div>
        {totalVisits === 0 ? <p className="muted" style={{ marginTop: 8 }}>No visits in the last 30 days.</p> : null}
      </section>

      <div className="grid-2">
        <section className="section" style={{ borderBottom: "none", marginBottom: 0 }}>
          <div className="section-head">
            <h2>Recent visits</h2>
            {data.recentVisits.length > 8 ? (
              <button
                type="button"
                className="section-head__action"
                onClick={() => setShowAllVisits((v) => !v)}
              >
                {showAllVisits ? "Show less" : `View all (${data.recentVisits.length})`}
              </button>
            ) : null}
          </div>
          <div className="table-wrap">
            <table className="table table--responsive">
              <thead>
                <tr>
                  <th>Visitor</th>
                  <th>Space</th>
                  <th>When</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {visitsShown.map((v) => (
                  <tr
                    key={v.view_id}
                    className="table-row--clickable"
                    onClick={() => {
                      window.location.href = `/rooms/${v.room_id}?tab=analytics`;
                    }}
                  >
                    <td data-label="Visitor">
                      {v.name || v.email || "Unknown"}
                      {v.is_internal ? <span className="badge warn"> Internal</span> : null}
                    </td>
                    <td data-label="Space">
                      <Link to={`/rooms/${v.room_id}`} onClick={(e) => e.stopPropagation()}>
                        {v.room_name}
                      </Link>
                    </td>
                    <td className="muted" data-label="When" title={new Date(v.started_at).toLocaleString()}>
                      {relativeTime(v.started_at)}
                    </td>
                    <td data-label="Time">{formatDwell(v.dwell_ms)}</td>
                  </tr>
                ))}
                {!data.recentVisits.length && (
                  <tr>
                    <td colSpan={4} className="muted">
                      No visits yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="section" style={{ borderBottom: "none", marginBottom: 0 }}>
          <div className="section-head">
            <h2>Most engaged</h2>
          </div>
          <div className="table-wrap">
            <table className="table table--responsive">
              <thead>
                <tr>
                  <th>Contact</th>
                  <th>Score</th>
                  <th>Signal</th>
                  <th>7d</th>
                </tr>
              </thead>
              <tbody>
                {data.mostEngaged.map((c) => (
                  <tr
                    key={`${c.email}-${c.room_id ?? c.room_name}`}
                    className={c.room_id ? "table-row--clickable" : undefined}
                    onClick={() => {
                      if (c.room_id) window.location.href = `/rooms/${c.room_id}?tab=recipients`;
                    }}
                  >
                    <td data-label="Contact">{c.name || c.email}</td>
                    <td data-label="Score">{c.score}</td>
                    <td data-label="Signal">
                      <span className="badge">{c.label}</span>
                    </td>
                    <td data-label="7d">
                      {c.momentum_7d > 0 ? "↑" : c.momentum_7d < 0 ? "↓" : "→"} {c.momentum_7d}
                    </td>
                  </tr>
                ))}
                {!data.mostEngaged.length && (
                  <tr>
                    <td colSpan={4} className="muted">
                      Engagement appears after page-level analytics.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
